import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  TextField,
  Button,
  Grid,
  Divider,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateCampaignMutation,
  useGetCampaignBatchNumbersQuery,
} from "../../../RTKQuery/CampaignService/campaignApi";
import Loader from "../../Loader/Loader";
import {
  modalContainer,
  modalContent,
  modalHeader,
  modalTitle,
  divider,
  formSection,
  sectionTitle,
  formGrid,
  fieldLabel,
  formField,
  helperText,
  domainSelect,
  selectedDomainChip,
  nextBatchInfoBox,
  nextBatchInfoText,
  buttonContainer,
  submitButton,
  cancelButton,
  successContainer,
  successHeader,
  successIcon,
  successTitle,
  successMessage,
  successMetaGrid,
  successMetaCard,
  successMetaLabel,
  successMetaValue,
  copySection,
  copySectionCard,
  copySectionLabel,
  copyFieldContainer,
  copyTextField,
  copyButton,
  loaderContainer,
} from "./styles";

const SENDGRID_DOMAINS = [
  "news@flavorwiki-newsletter.com",
  "daniel.protz@flavorwiki.co",
  "daniel@flavorwiki.international",
  "daniel@flavorwiki.ch",
  "marco@flavorwiki.biz",
];

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  subject: Yup.string(),
  campaignSource: Yup.string().required("Campaign source is required"),
  batchCount: Yup.number()
    .typeError("Batch count must be a number")
    .integer("Batch count must be a whole number")
    .min(1, "Batch count must be at least 1")
    .required("Batch count is required"),
  domain: Yup.array()
    .of(Yup.string())
    .min(1, "At least one domain is required")
    .required("Domain is required"),
});

const CampaignNameGenerationModal = () => {
  const [autoGeneratedTitle, setAutoGeneratedTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [created, setCreated] = useState(false);
  const [data, setData] = useState();
  const [createCampaign, { isLoading: creating }] = useCreateCampaignMutation();
  const { data: batchNumbersData, isLoading: loadingBatchNumbers } =
    useGetCampaignBatchNumbersQuery();

  const normalizeBatchNumber = (value) =>
    String(value ?? "")
      .replace(/\D/g, "")
      .slice(0, 5)
      .padStart(5, "0");

  const nextBatchNumber = useMemo(() => {
    const rawBatchEntries = Array.isArray(batchNumbersData)
      ? batchNumbersData
      : batchNumbersData?.batches || [];

    let maxBatch = 0;
    rawBatchEntries.forEach((entry) => {
      const batchValue = Number(normalizeBatchNumber(entry?.batch));
      if (!Number.isNaN(batchValue)) {
        maxBatch = Math.max(maxBatch, batchValue);
      }
    });

    return String(maxBatch + 1).padStart(5, "0");
  }, [batchNumbersData]);

  const createdCampaigns = useMemo(
    () =>
      Array.isArray(data)
        ? data
        : Array.isArray(data?.created)
          ? data.created
        : Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data?.data?.created)
            ? data.data.created
            : Array.isArray(data?.data?.campaigns)
              ? data.data.campaigns
          : data
            ? [data]
            : [],
    [data],
  );

  const createdCampaignTitles = useMemo(() => {
    const titles = createdCampaigns
      .map((campaign) => campaign?.campaignTitle)
      .filter(Boolean);

    if (titles.length > 0) {
      return titles;
    }

    return autoGeneratedTitle ? [autoGeneratedTitle] : [];
  }, [createdCampaigns, autoGeneratedTitle]);

  const createdBatchNumber =
    createdCampaigns[0]?.batchNumber || Number(nextBatchNumber);

  const allCampaignTitlesText = useMemo(
    () => createdCampaignTitles.join("\n"),
    [createdCampaignTitles],
  );

  const formik = useFormik({
    initialValues: {
      title: "",
      subject: "",
      campaignSource: "Sendgrid",
      batchCount: 1,
      domain: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      setSubject(values.subject);
      const count = Number(values.batchCount);
      const campaignNumbers = Array.from({ length: count }, (_, index) =>
        index + 1
      );
      const payload = {
        campaignName: values.title,
        campaignSubject: values.subject,
        campaignSource: values.campaignSource,
        batchNumber: Number(nextBatchNumber),
        batchCount: count,
        campaignNumbers,
        sentFromEmails: values.domain,
      };

      try {
        const response = await createCampaign(payload).unwrap();
        setData(response);
        setCreated(true);
      } catch (error) {
        console.error("Failed to create campaign", error);
      }
    },
  });

  useEffect(() => {
    const { title } = formik.values;
    const generatedCode = nextBatchNumber ? `COC${nextBatchNumber}` : "";
    const formattedTitle = [generatedCode, title]
      .filter((value) => value)
      .join("_");

    setAutoGeneratedTitle(formattedTitle);
  }, [formik.values.title, nextBatchNumber]);

  // Reset domain when campaign source changes
  useEffect(() => {
    formik.setFieldValue("domain", []);
  }, [formik.values.campaignSource]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleCopyAllTitles = () => {
    navigator.clipboard.writeText(allCampaignTitlesText);
    setSnackbarOpen(true);
  };

  const handleCopySubject = () => {
    navigator.clipboard.writeText(subject);
    setSnackbarOpen(true);
  };

  const onClose = () => {
    setData(null);
    setCreated(false);
    formik.resetForm();
    setOpen(false);
  };

  return (
    <div>
      <Button
        onClick={handleOpen}
        variant="outlined"
        sx={{ textTransform: "none" }}
      >
        Create Campaign
      </Button>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="campaign-modal-title"
        aria-describedby="campaign-modal-description"
      >
        <Paper sx={modalContainer}>
          <Box sx={modalContent}>
            <Box sx={modalHeader}>
              <Box>
                <Typography id="campaign-modal-title" sx={modalTitle}>
                  Create New Campaign
                </Typography>
                <Typography
                  sx={{ color: "#6b7280", fontSize: "0.85rem", mt: "0.2rem" }}
                >
                  Set up a campaign batch in just a few steps.
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{ color: "#6b7280" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={divider} />

            {!created && !creating ? (
              <form onSubmit={formik.handleSubmit}>
                <Box sx={formSection}>
                  <Typography sx={sectionTitle}>Campaign Details</Typography>
                  <Grid container spacing={2} sx={formGrid}>
                    <Grid item xs={12}>
                      <Typography sx={fieldLabel}>Campaign Name</Typography>
                      <TextField
                        placeholder="Enter campaign name"
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.title && Boolean(formik.errors.title)
                        }
                        helperText={formik.touched.title && formik.errors.title}
                        fullWidth
                        size="small"
                        required
                        sx={formField}
                        FormHelperTextProps={{ sx: helperText }}
                      />
                    </Grid>

                    {/* Subject field hidden for now */}
                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Batch Size</Typography>
                      <TextField
                        placeholder="How many campaigns?"
                        name="batchCount"
                        type="number"
                        value={formik.values.batchCount}
                        onChange={formik.handleChange}
                        error={
                          formik.touched.batchCount &&
                          Boolean(formik.errors.batchCount)
                        }
                        helperText={
                          formik.touched.batchCount && formik.errors.batchCount
                        }
                        fullWidth
                        size="small"
                        required
                        inputProps={{ min: 1, step: 1 }}
                        sx={formField}
                        FormHelperTextProps={{ sx: helperText }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Campaign Source</Typography>
                      <FormControl fullWidth size="small" required sx={formField}>
                        <Select
                          name="campaignSource"
                          value={formik.values.campaignSource}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.campaignSource &&
                            Boolean(formik.errors.campaignSource)
                          }
                        >
                          <MenuItem value="Sendgrid">Sendgrid</MenuItem>
                          <MenuItem value="Apollo">Apollo</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography sx={fieldLabel}>Domain</Typography>
                      <FormControl fullWidth size="small" required sx={formField}>
                        <Select
                          name="domain"
                          multiple
                          value={formik.values.domain}
                          onChange={(e) => {
                            formik.setFieldValue("domain", e.target.value);
                          }}
                          input={<OutlinedInput />}
                          sx={domainSelect}
                          error={
                            formik.touched.domain &&
                            Boolean(formik.errors.domain)
                          }
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return (
                                <Typography
                                  sx={{
                                    color: "#9ca3af",
                                    fontSize: "0.86rem",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Select domain(s)
                                </Typography>
                              );
                            }
                            return selected.map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                sx={selectedDomainChip}
                              />
                            ));
                          }}
                        >
                          {formik.values.campaignSource === "Apollo" ? (
                            [
                              <MenuItem
                                key="marco@flavorwiki.biz"
                                value="marco@flavorwiki.biz"
                              >
                                <Checkbox
                                  checked={
                                    formik.values.domain.indexOf(
                                      "marco@flavorwiki.biz",
                                    ) > -1
                                  }
                                />
                                <ListItemText primary="marco@flavorwiki.biz" />
                              </MenuItem>,
                              <MenuItem
                                key="marco@flavorwiki.net"
                                value="marco@flavorwiki.net"
                              >
                                <Checkbox
                                  checked={
                                    formik.values.domain.indexOf(
                                      "marco@flavorwiki.net",
                                    ) > -1
                                  }
                                />
                                <ListItemText primary="marco@flavorwiki.net" />
                              </MenuItem>,
                              <MenuItem
                                key="marco@flavorwiki.ch"
                                value="marco@flavorwiki.ch"
                              >
                                <Checkbox
                                  checked={
                                    formik.values.domain.indexOf(
                                      "marco@flavorwiki.ch",
                                    ) > -1
                                  }
                                />
                                <ListItemText primary="marco@flavorwiki.ch" />
                              </MenuItem>,
                              <MenuItem
                                key="daniel@flavorwiki.com"
                                value="daniel@flavorwiki.com"
                              >
                                <Checkbox
                                  checked={
                                    formik.values.domain.indexOf(
                                      "daniel@flavorwiki.com",
                                    ) > -1
                                  }
                                />
                                <ListItemText primary="daniel@flavorwiki.com" />
                              </MenuItem>,
                            ]
                          ) : (
                            SENDGRID_DOMAINS.map((email) => (
                              <MenuItem key={email} value={email}>
                                <Checkbox
                                  checked={
                                    formik.values.domain.indexOf(email) > -1
                                  }
                                />
                                <ListItemText primary={email} />
                              </MenuItem>
                            ))
                          )}
                        </Select>
                        {formik.touched.domain && formik.errors.domain && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={helperText}
                          >
                            {formik.errors.domain}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={nextBatchInfoBox}>
                        <Typography sx={nextBatchInfoText}>
                          Next Batch Number:{" "}
                          <strong>
                            {loadingBatchNumbers ? "Loading..." : nextBatchNumber}
                          </strong>
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={buttonContainer}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        onClick={onClose}
                        variant="outlined"
                        fullWidth
                        sx={cancelButton}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={submitButton}
                      >
                        Create Campaign
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </form>
            ) : created && data ? (
              <Box sx={successContainer}>
                <Box sx={successHeader}>
                  <CheckCircleIcon sx={successIcon} />
                  <Box>
                    <Typography sx={successTitle}>
                      Campaign Batch Created Successfully!
                    </Typography>
                    <Typography sx={successMessage}>
                      {`Created ${createdCampaignTitles.length || 0} campaign${
                        createdCampaignTitles.length === 1 ? "" : "s"
                      }. Copy the titles below.`}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={copySection}>
                  <Grid container spacing={1.2} sx={successMetaGrid}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={successMetaCard}>
                        <Typography sx={successMetaLabel}>Batch Number</Typography>
                        <Typography sx={successMetaValue}>
                          {String(createdBatchNumber).padStart(5, "0")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={successMetaCard}>
                        <Typography sx={successMetaLabel}>Campaigns Created</Typography>
                        <Typography sx={successMetaValue}>
                          {createdCampaignTitles.length}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={copySectionCard}>
                  <Typography sx={copySectionLabel}>Campaign Titles</Typography>
                  <Box sx={copyFieldContainer}>
                    <TextField
                      value={allCampaignTitlesText}
                      fullWidth
                      size="small"
                      multiline
                      minRows={3}
                      maxRows={8}
                      disabled
                      sx={copyTextField}
                    />
                    <Button
                      onClick={handleCopyAllTitles}
                      startIcon={<ContentCopyIcon />}
                      variant="outlined"
                      sx={copyButton}
                      disabled={!allCampaignTitlesText}
                    >
                      Copy All
                    </Button>
                  </Box>
                </Box>

                {subject ? (
                  <Box sx={copySection}>
                    <Typography sx={copySectionLabel}>
                      Campaign Subject
                    </Typography>
                    <Box sx={copyFieldContainer}>
                      <TextField
                        value={subject}
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        disabled
                        sx={copyTextField}
                      />
                      <Button
                        onClick={handleCopySubject}
                        startIcon={<ContentCopyIcon />}
                        variant="outlined"
                        sx={copyButton}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Box>
                ) : null}

                <Box sx={buttonContainer}>
                  <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={submitButton}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            ) : creating ? (
              <Box sx={loaderContainer}>
                <Loader />
              </Box>
            ) : null}
          </Box>
        </Paper>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Text copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
};

export default CampaignNameGenerationModal;
